import React, { useState, useEffect } from "react";
import { Table, Card, Button, Space, Typography, message, Popconfirm, Tag } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import clientApi from "../../clients/store/api";

const { Title } = Typography;

const AdminEntitlementsList = () => {
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    fetchEntitlements();
  }, []);

  const fetchEntitlements = async () => {
    try {
      setLoading(true);
      const response = await clientApi.getEntitlements();
      setEntitlements(response || []);
    } catch (error) {
      console.error("Error fetching entitlements:", error);
      message.error("Failed to load entitlements");
      setEntitlements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entitlement) => {
    history.push(`/admin/billing/entitlements/${entitlement.id}`);
  };

  const handleDelete = async (entitlement) => {
    try {
      // This will soft delete (set isActive = false)
      await clientApi.deleteEntitlement(entitlement.id);
      message.success("Entitlement deactivated successfully");
      fetchEntitlements(); // Refresh list
    } catch (error) {
      console.error("Error deleting entitlement:", error);
      message.error("Failed to deactivate entitlement");
    }
  };

  const handleActivate = async (entitlement) => {
    try {
      await clientApi.activateEntitlement(entitlement.id);
      message.success("Entitlement activated successfully");
      fetchEntitlements(); // Refresh list
    } catch (error) {
      console.error("Error activating entitlement:", error);
      message.error("Failed to activate entitlement");
    }
  };

  const handleCreate = () => {
    history.push("/admin/billing/entitlements/new");
  };

  const formatPrice = (cost, period, term) => {
    if (!cost || cost === 0) return "Free";

    let periodSuffix = "";
    // Use period if available, otherwise fall back to term for backward compatibility
    if (period) {
      switch (period) {
        case "each":
          periodSuffix = " /ea";
          break;
        case "daily":
          periodSuffix = " /day";
          break;
        case "weekly":
          periodSuffix = " /week";
          break;
        case "monthly":
          periodSuffix = " /month";
          break;
        case "yearly":
          periodSuffix = " /year";
          break;
        default:
          periodSuffix = "";
      }
    } else if (term === 0) {
      periodSuffix = " /ea";
    } else if (term === 30) {
      periodSuffix = " /month";
    }

    const numericCost = parseFloat(cost);

    // If amount is less than $1, show as cents
    if (numericCost < 1) {
      const cents = numericCost * 100;
      const centsFormatted =
        cents % 1 === 0 ? cents.toFixed(0) : cents.toFixed(1).replace(/\.?0+$/, "");
      return `${centsFormatted}¢${periodSuffix}`;
    }

    // For dollar amounts
    if (numericCost % 1 === 0) {
      return `$${numericCost.toFixed(0)}${periodSuffix}`;
    } else {
      return `$${numericCost.toFixed(2)}${periodSuffix}`;
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "simpleId",
      key: "simpleId",
      width: 60,
      sorter: (a, b) => a.simpleId - b.simpleId
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true
    },
    {
      title: "Price",
      key: "price",
      width: 150,
      render: (_, record) => (
        <span style={{ color: "#1890ff", fontWeight: "500" }}>
          {formatPrice(record.cost, record.period, record.term)}
        </span>
      ),
      sorter: (a, b) => (parseFloat(a.cost) || 0) - (parseFloat(b.cost) || 0)
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Inactive"}</Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      width: 120
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          {record.isActive ? (
            <Popconfirm
              title="Deactivate Entitlement"
              description="Are you sure you want to deactivate this entitlement? It will be hidden from clients."
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Activate Entitlement"
              description="Are you sure you want to activate this entitlement? It will be available to clients."
              onConfirm={() => handleActivate(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                icon={<CheckOutlined />}
                size="small"
                style={{ color: "#52c41a" }}
              >
                Activate
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
      width: 150
    }
  ];

  return (
    <div className="admin-entitlements-list">
      <style>
        {`
          .inactive-entitlement-row {
            background-color: #f5f5f5 !important;
            opacity: 0.6;
          }
          .inactive-entitlement-row:hover {
            background-color: #e6f7ff !important;
          }
        `}
      </style>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px"
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Entitlements Management
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
          >
            Create New Entitlement
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={entitlements}
          loading={loading}
          rowKey="id"
          rowClassName={(record) => (!record.isActive ? "inactive-entitlement-row" : "")}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entitlements`
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default AdminEntitlementsList;
